import { useEffect } from 'react';

/**
 * Re-runs `reload` the moment this tab becomes visible again, and optionally on a
 * fixed interval the whole time it stays visible.
 *
 * A hook that loads once on mount and never again has no way to learn about a change
 * made from another device, or another tab, while this one sat in the background -
 * training logged from a phone, hydration added elsewhere, a profile edited on
 * another device. Revalidating on visibility catches that promptly without polling
 * while nobody is even looking at the tab, but it does nothing for a tab that is
 * simply left open and visible for a while - `intervalMs` is what covers that case,
 * skipping ticks while hidden so a backgrounded tab still is not polled for nothing.
 * Same fix already used for the session check in `SessionProvider`, factored out once
 * it was needed a second time.
 *
 * @param {() => void} reload
 * @param {{intervalMs?: number}} [options]  `intervalMs` matches
 *   `INTEGRATION_POLL_MS`, the same cadence the server itself polls FatSecret and
 *   Open Wearables at, for the same reason: a change landing on the server should not
 *   wait on a slower poll of its own before an open tab notices it.
 */
export function useRevalidateOnVisible(reload, { intervalMs } = {}) {
  useEffect(() => {
    const onVisibilityChange = () => {
      if (globalThis.document?.visibilityState === 'visible') reload();
    };
    globalThis.document?.addEventListener('visibilitychange', onVisibilityChange);
    const timer = intervalMs ? globalThis.setInterval(() => {
      if (globalThis.document?.visibilityState !== 'hidden') reload();
    }, intervalMs) : null;
    return () => {
      globalThis.document?.removeEventListener('visibilitychange', onVisibilityChange);
      if (timer !== null) globalThis.clearInterval(timer);
    };
  }, [reload, intervalMs]);
}
